import {
  cacheKeys,
  Issue,
  useIsAssetRoutineIdpInProgress,
  useIssue
} from '@gripp/shared-logic';
import {
  IdpEditLockedHint,
  LoadingIndicator,
  RoutineExecutionProps,
  RoutineForm,
  SlideAction,
  tokens,
  useSlideLayout
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { IssueEditRoutineLoader } from '../../../../../libs/shared-ui/src/components/issue/issueEditRoutineLoader';

export const ActivityReviseColumn = () => {
  const { issueId } = useParams();
  const { issue: issueData, isLoading } = useIssue(issueId ?? '');
  const issue = issueData as unknown as Issue | undefined;
  const { onSlideChange } = useSlideLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [routineExecutionProps, setRoutineExecutionProps] =
    useState<RoutineExecutionProps | null>(null);
  const [isLoadingRoutine, setIsLoadingRoutine] = useState(true);

  // Defensive guard for this route: the "Revise Submission" CardButton
  // (issueEdit.tsx) is the normal entry point and is disabled while
  // extraction is in flight, but this URL is directly reachable (a stale
  // bookmark, browser back/forward). Live-updates via the same poll the
  // CardButton's disable uses, so a submission that finishes while this panel
  // is open reveals the form without a refresh.
  const isReviseIdpLocked = useIsAssetRoutineIdpInProgress(
    issue?.assetRoutine
  );

  const handleBack = async () => {
    await onSlideChange(SlideAction.PREV);
    navigate('..', { relative: 'path' });
  };

  if (isLoading || !issue) {
    return <LoadingIndicator />;
  }

  return (
    <View style={{ flex: 1 }}>
      <IssueEditRoutineLoader
        isEditRoutineLoader={isLoadingRoutine}
        issue={issue}
        currentTitle={issue.title}
        onRoutineEdition={(props) => {
          setIsLoadingRoutine(false);
          setRoutineExecutionProps(props);
        }}
        onAlertWorkflowDetected={() => {
          setIsLoadingRoutine(false);
          handleBack();
        }}
      />
      {isReviseIdpLocked ? (
        <IdpEditLockedHint
          style={styles.idpLockedHint}
          testID="activity-revise-idp-locked"
        />
      ) : isLoadingRoutine || !routineExecutionProps ? (
        <LoadingIndicator />
      ) : (
        <RoutineForm
          {...routineExecutionProps}
          onCompleted={async () => {
            await queryClient.invalidateQueries({
              queryKey: cacheKeys('issueMessage').all,
              exact: false,
            });
            handleBack();
          }}
          onCancel={handleBack}
          topBar={() => <></>}
          isIssueEditRoutine={true}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Typography/color come from IdpEditLockedHint; only spacing is local.
  idpLockedHint: {
    margin: tokens.spacing.lg,
  },
});
